import {
  AngularAsyncValidator,
  ExtendedAbstractControl,
  ExtendedAsyncValidateFunction,
  ExtendedAsyncValidatorConfig
} from '@aviary/forms/extensions/form-extensions.types';
import {hasValue, isBoolean, isDefined, isFunction, isObject, isPopulatedString} from '@aviary/store/common/typing.helpers';
import {omitPropertiesByKeyFrom} from '@aviary/store/transformation.helpers';
import {hasChanged} from '@aviary/forms/form.helpers';
import {filter, map, of, take, tap} from 'rxjs';
import {ValidationErrors} from '@angular/forms';

export type AsyncValidatorRemoverResult = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Angular's validatorOrOpts arguments are typed as any, so we maintain that type.
  validatorOrOptsWithoutAsyncValidators: any;
  addRemovedAsyncValidatorsTo: (targetControl: ExtendedAbstractControl) => void
};

/**
 * Angular runs async validators immediately, which is before we can initialize our properties -- too early!  So we remove any async validators so that the
 * caller can instantiate without them.  And we also return a function so that the caller can add the async validators back in after it's safely constructed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Angular's validatorOrOpts arguments are typed as any, so we maintain that type.
export function removeAsyncValidatorsFrom(validatorOrOpts?: any, asyncValidator?: AngularAsyncValidator | null): AsyncValidatorRemoverResult {
  const validatorOrOptsIsObject: boolean = isObject(validatorOrOpts) && !isFunction(validatorOrOpts);

  const asyncValidatorsFromValidatorOrOpts: AngularAsyncValidator | null | undefined =
    validatorOrOptsIsObject ? validatorOrOpts.asyncValidators : undefined;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Angular's validatorOrOpts arguments are typed as any, so we maintain that type.
  const validatorOrOptsWithoutAsyncValidators: any =
    validatorOrOptsIsObject ? omitPropertiesByKeyFrom(validatorOrOpts, 'asyncValidators') : validatorOrOpts;

  function addRemovedAsyncValidatorsTo(targetControl: ExtendedAbstractControl): void {
    if (hasValue(asyncValidatorsFromValidatorOrOpts)) targetControl.addAsyncValidators(asyncValidatorsFromValidatorOrOpts);
    if (hasValue(asyncValidator)) targetControl.addAsyncValidators(asyncValidator);

    // If the previous two lines added anything, update our validity, but don't emit an event because Angular doesn't in its form entity constructors.
    if (hasValue(targetControl.asyncValidator)) targetControl.updateValueAndValidity({onlySelf: true, emitEvent: false});
  }

  return {validatorOrOptsWithoutAsyncValidators, addRemovedAsyncValidatorsTo};
}

export function createExtendedAsyncValidator(config: ExtendedAsyncValidatorConfig): ExtendedAsyncValidateFunction {
  interface ValidationResult {
    readonly controlValue: string;
    readonly wasValid: boolean;
  }

  function returnValueFor(valid: boolean): ValidationErrors | null {
    if (valid) return null;

    return {
      [config.validationErrorKey]: true,
      ...isPopulatedString(config.overrideErrorMessage) ? {overrideErrorMessage: config.overrideErrorMessage} : {}
    };
  }

  // Remember a separate "most recently validated" value for each control.  And since this is a WeakMap, keys will be cleaned up as controls go out of scope.
  const mostRecentValidationResultByControl: WeakMap<ExtendedAbstractControl, ValidationResult> = new WeakMap();

  return control => {
    // Skip validation if the validateOnlyIf function exists and returns false.  No need to check anything else if this condition isn't satisfied.
    if (isDefined(config.validateOnlyIf) && !config.validateOnlyIf(control)) return of(null);

    const controlValue: string | undefined = control.value;

    // Skip validation if there's no value to validate.
    if (!isPopulatedString(controlValue)) return of(null);

    // Skip validation if the value is still the initial value.  Validating existing values when editing would trigger false errors.
    if (!hasChanged(control)) return of(null);

    const mostRecentResult: ValidationResult | undefined = mostRecentValidationResultByControl.get(control);

    // Skip validation if the value is still the most recently validated value.  No need to revalidate, whether it was valid or not.
    if (controlValue === mostRecentResult?.controlValue) return of(returnValueFor(mostRecentResult.wasValid));

    config.store.dispatch(config.validationActionCreator(controlValue));

    return config.store.select(config.validitySelector).pipe(
      filter(isBoolean),
      take(1), // This observable must complete, or the form that uses this validator will be stuck in the pending state.
      tap(valid => {
        mostRecentValidationResultByControl.set(control, {controlValue, wasValid: valid});
      }),
      map(returnValueFor)
    );
  };
}
